import { useShouldExcludeUser } from './useShouldExcludeUser.ts';
import { useSiteUserTraits } from './useSiteUserTraits.ts';

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

/**
 * Hook to determine if the current user is a new user based on their first active date.
 * @param param0 - Configuration options
 * @param param0.activeDaysThreshold - Number of days to consider a user as new (default is 28 days)
 * @param param0.activeUserCutoffDate - Optional cutoff date to determine new user status
 * @returns Object containing isNewUser boolean, loading state, and error if any
 */
export const useIsNewUser = (
	{
		activeDaysThreshold = 28,
		activeUserCutoffDate,
	}: {
		activeDaysThreshold?: number;
		activeUserCutoffDate?: Date;
	},
	skip = false,
):
	| {
			isNewUser: null;
			loading: boolean;
			error?: undefined;
	  }
	| {
			isNewUser: null;
			error: Error;
			loading?: undefined;
	  }
	| {
			isNewUser: boolean | null;
			loading: boolean;
			error: null;
	  } => {
	const { data, loading, error } = useSiteUserTraits({ skipBootstrap: skip });

	const confluenceFirstActive = data?.attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = confluenceFirstActive ? new Date(confluenceFirstActive) : null;

	const { isNewlyCreatedUser } = useShouldExcludeUser({
		checkDate: firstActiveDate,
		daysThreshold: activeDaysThreshold,
		cutoffDate: activeUserCutoffDate,
	});

	if (loading) return { isNewUser: null, loading: true };
	if (error) return { isNewUser: null, error };

	return { isNewUser: isNewlyCreatedUser, loading: false, error: null };
};
